import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../../services/api';
import * as fileApi from '../../../services/fileApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import ProofAttachmentDropzone from '../../Common/ProofAttachmentDropzone';
import { getCrmPublicSetting } from '../../../services/crmApi';
import {
  CreditCardIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { getSlimLogoPath } from '../../../utils/slimLogo';
import { toPaymentDateTimeIso } from '../../../utils/crmPaymentFinanceDefaults';

const roundCurrency = (value) => Math.round((Number(value) || 0) * 100) / 100;

const DEFAULT_PAYMENT_POLICY = `Institution Payment & Refund Policy

1. Non-Refundable Payments
All fees and payments submitted through this portal are strictly non-refundable once the transaction is processed, subject to the exceptions below.

2. Incorrect Payments
If you believe a payment was made in error (wrong amount, duplicate charge), contact us within 7 days of the transaction date with your payment reference number and supporting documents for review.

3. Program Cancellation by the Institution
If the institution cancels a program or event before commencement, enrolled participants will be offered a full refund or credit transfer to an equivalent program at the institution's discretion.

4. Partial Refunds
Partial refunds are only considered where explicitly stated in the specific program's enrollment agreement and must be requested in writing within the specified timeframe.

5. Payment Methods
The institution accepts card payments (credit/debit), bank transfers, and other methods as displayed at checkout. Card payments are processed through a secure third-party payment gateway.

6. Receipt & Records
A payment confirmation will be sent to your registered email address. Please retain this for your records. The institution is not responsible for payments made to incorrect accounts due to applicant error.

7. Disputes
Any payment disputes must be raised directly with the institution before initiating a chargeback through your bank. Unauthorized chargebacks may result in the suspension of your application or enrollment.

8. Contact
For payment-related queries, please contact our Finance Office during business hours.`;

const PolicyModal = ({ title, content, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="overflow-y-auto px-5 py-4 flex-1">
        <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{content}</pre>
      </div>
      <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

const PublicPaymentSubmit = () => {
  const { token } = useParams();
  const location = useLocation();
  const [paymentVerification, setPaymentVerification] = useState(null);
  const [applicationPayment, setApplicationPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [paymentTermsAgreed, setPaymentTermsAgreed] = useState(false);
  const [paymentPolicyText, setPaymentPolicyText] = useState('');
  const [showPaymentPolicyModal, setShowPaymentPolicyModal] = useState(false);

  // Payment form state
  const [paymentData, setPaymentData] = useState({
    actualAmount: '',
    paymentMethod: 'Bank Transfer',
    paymentDate: new Date().toISOString().split('T')[0],
    referenceNumber: '',
    bankName: '',
    notes: ''
  });
  
  const [availableCredit, setAvailableCredit] = useState(0);
  // TEMPORARILY DISABLED: applying student credit on the public payment link is switched off
  // while credit balances are being audited. Set to false to re-enable.
  const creditApplyDisabled = true;
  const [applyCredit, setApplyCredit] = useState(false);
  const [creditToApply, setCreditToApply] = useState(0);

  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [gatewayCardReturnStatus, setGatewayCardReturnStatus] = useState(null);
  const [gatewaySubmittedAmount, setGatewaySubmittedAmount] = useState(null);
  const lastHandledCardReturnRef = useRef('');

  useEffect(() => {
    fetchPaymentVerificationByToken();
  }, [token]);

  // Fetch institution payment policy text (public endpoint, no auth required)
  useEffect(() => {
    getCrmPublicSetting('institutionPaymentPolicy')
      .then((res) => {
        const text = res?.data?.data;
        if (text) setPaymentPolicyText(text);
      })
      .catch(() => {
        // Non-blocking — default text shown if fetch fails
      });
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const cardPaymentStatus = searchParams.get('cardPayment'); // success|failed|cancelled
    const reqid = searchParams.get('reqid');
    const responseCode = searchParams.get('responseCode');

    if (!cardPaymentStatus) return;

    const returnKey = `${cardPaymentStatus || ''}:${reqid || ''}:${responseCode || ''}`;
    if (lastHandledCardReturnRef.current === returnKey) return;
    lastHandledCardReturnRef.current = returnKey;

    if (cardPaymentStatus === 'success') {
      toast.success('Card payment completed successfully.');
      setGatewayCardReturnStatus('success');
      // Refresh latest backend state after gateway return.
      fetchPaymentVerificationByToken();
      const currentDue = applicationPayment?.applicationPayment?.amount?.remainingBalance ||
        applicationPayment?.applicationPayment?.amount?.expectedAmount ||
        paymentVerification?.actualRemainingAmount ||
        paymentVerification?.payment?.expectedAmount ||
        0;
      if (Number(currentDue) > 0) {
        setGatewaySubmittedAmount(Number(currentDue));
      }
    } else if (cardPaymentStatus === 'failed') {
      setGatewayCardReturnStatus('failed');
      setGatewaySubmittedAmount(null);
      toast.error(`Card payment failed${responseCode ? ` (code: ${responseCode})` : ''}. Please try again.`);
    } else if (cardPaymentStatus === 'cancelled') {
      setGatewayCardReturnStatus('cancelled');
      setGatewaySubmittedAmount(null);
      toast('Card payment was cancelled. You can retry the payment.', { icon: '⚠️' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchPaymentVerificationByToken = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try ApplicationPayment first (new system)
      try {
        const appPaymentResponse = await api.get(`/crm/application-payments/public/${token}`, {
          skipErrorToast: true
        });
        const appPaymentData = appPaymentResponse.data.data;
        setApplicationPayment(appPaymentData);
        
        const credBal = Number(appPaymentData.studentCredit?.creditBalance || appPaymentData.applicationPayment?.amount?.creditBalance || 0);
        setAvailableCredit(credBal);

        // Pre-fill expected amount and reference number (but allow user to change it)
        const prefillAmountRaw = appPaymentData.applicationPayment?.amount?.remainingBalance ||
                             appPaymentData.applicationPayment?.amount?.expectedAmount || '';
        const prefillAmount = prefillAmountRaw === '' ? '' : roundCurrency(prefillAmountRaw);
        setPaymentData(prev => ({
          ...prev,
          actualAmount: prefillAmount,
          referenceNumber: appPaymentData.application?.applicationNumber || ''
        }));
        
        setLoading(false);
        return;
      } catch (appPaymentError) {
        // If link is explicitly expired (403), stop immediately and display expiration message
        if (appPaymentError.response?.status === 403) {
          setError(appPaymentError.response?.data?.message || 'Payment link has expired. Please contact the institution for a new link.');
          setLoading(false);
          return;
        }
        // If ApplicationPayment not found (404), try PaymentVerification (legacy system)
        console.error('ApplicationPayment not found, trying PaymentVerification...', appPaymentError.response?.data || appPaymentError.message);
      }
      
      // Fallback to PaymentVerification (legacy system)
      const response = await api.get(`/crm/payment-verification/public/${token}`, {
        skipErrorToast: true
      });
      const data = response.data.data || response.data;
      setPaymentVerification(data);
      
      // Pre-fill expected amount and reference number (application number)
      // Use actualRemainingAmount if available (for partial payments), otherwise use expectedAmount
      const legacyPrefillRaw = data.actualRemainingAmount || data.payment?.expectedAmount || '';
      setPaymentData(prev => ({
        ...prev,
        actualAmount: legacyPrefillRaw === '' ? '' : roundCurrency(legacyPrefillRaw),
        referenceNumber: data.applicationId?.applicationNumber || ''
      }));
    } catch (error) {
      console.error('Error fetching payment information:', error);
      setError(error.response?.data?.message || 'Invalid or expired payment link. Please contact the institution.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleApplyCredit = (checked) => {
    if (creditApplyDisabled) return;
    setApplyCredit(checked);
    const amountDueRaw = applicationPayment?.applicationPayment?.amount?.remainingBalance || 
                     applicationPayment?.applicationPayment?.amount?.expectedAmount ||
                     paymentVerification?.actualRemainingAmount || 
                     paymentVerification?.payment?.expectedAmount || 0;
    const amountDue = roundCurrency(amountDueRaw);
    if (checked) {
      const initialCredit = Math.min(availableCredit, amountDue);
      setCreditToApply(initialCredit);
      const remainingCash = Math.max(0, roundCurrency(amountDue - initialCredit));
      setPaymentData(prev => ({ ...prev, actualAmount: remainingCash }));
    } else {
      setCreditToApply(0);
      setPaymentData(prev => ({ ...prev, actualAmount: amountDue }));
    }
  };

  const handleCreditToApplyChange = (val) => {
    const amountDueRaw = applicationPayment?.applicationPayment?.amount?.remainingBalance || 
                     applicationPayment?.applicationPayment?.amount?.expectedAmount ||
                     paymentVerification?.actualRemainingAmount || 
                     paymentVerification?.payment?.expectedAmount || 0;
    const amountDue = roundCurrency(amountDueRaw);
    const maxApplicable = Math.min(availableCredit, amountDue);
    const parsed = parseFloat(val);
    const num = isNaN(parsed) ? 0 : Math.max(0, Math.min(maxApplicable, parsed));
    setCreditToApply(num);
    const remainingCash = Math.max(0, roundCurrency(amountDue - num));
    setPaymentData(prev => ({ ...prev, actualAmount: remainingCash }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    const appliedCreditNum = applyCredit ? roundCurrency(creditToApply) : 0;
    const actualAmountNum = roundCurrency(paymentData.actualAmount || 0);
    const totalCovered = roundCurrency(actualAmountNum + appliedCreditNum);

    const isFullCreditSettle = appliedCreditNum > 0 && actualAmountNum === 0;
    const methodRequiresReceipt = !isFullCreditSettle && !['Card', 'Credit Card', 'Debit Card', 'Cash'].includes(paymentData.paymentMethod);

    // Validation
    if (totalCovered <= 0) {
      toast.error('Please enter a valid payment amount or apply available credit balance');
      return;
    }

    if (isSubmittingRef.current || submitting || uploadingReceipt) {
      return;
    }

    if (!paymentData.paymentMethod && !isFullCreditSettle) {
      toast.error('Please select a payment method');
      return;
    }

    if (!paymentTermsAgreed) {
      toast.error('Please agree to the payment submission terms before submitting.');
      return;
    }

    isSubmittingRef.current = true;
    try {
      setSubmitting(true);

      const paymentAgreement = {
        agreed: true,
        agreedAtClient: new Date().toISOString(),
        source: 'public_payment_link_submission_form'
      };

      // If this is a card payment on a new-style ApplicationPayment link, route via Paycorp
      if (applicationPayment && ['Card', 'Credit Card', 'Debit Card'].includes(paymentData.paymentMethod)) {
        const cardPayload = {
          actualAmount: actualAmountNum,
          paymentMethod: paymentData.paymentMethod,
          paymentDate: toPaymentDateTimeIso(paymentData.paymentDate),
          notes: paymentData.notes,
          paymentAgreement
        };

        const response = await api.post(
          `/crm/application-payments/public/${token}/initiate-card`,
          cardPayload
        );

        const paymentPageUrl =
          response?.data?.data?.paymentPageUrl || response?.data?.data?.paymentUrl;

        if (!paymentPageUrl) {
          throw new Error('Payment gateway did not return a payment page URL');
        }

        toast.success('Redirecting to secure card payment page...');
        window.location.href = paymentPageUrl;
        return;
      }

      // For non-card methods (and legacy PaymentVerification links), require receipt when needed
      if (methodRequiresReceipt && !receiptFile) {
        isSubmittingRef.current = false;
        setSubmitting(false);
        toast.error('Please upload payment receipt/proof');
        return;
      }

      // Upload receipt first (only when required and provided)
      let documentId = null;
      if (methodRequiresReceipt && receiptFile) {
        setUploadingReceipt(true);
        const uploadResponse = await fileApi.uploadFilePublic(receiptFile, { 
          category: 'Payment Receipt',
          description: `Payment receipt for ${paymentData.paymentMethod}`
        });
        documentId = uploadResponse.data.data.fileId;
        setUploadingReceipt(false);
      }

      // Submit payment
      const payload = {
        actualAmount: actualAmountNum,
        creditAmountApplied: appliedCreditNum,
        paymentMethod: isFullCreditSettle ? 'Credit Balance' : paymentData.paymentMethod,
        paymentDate: toPaymentDateTimeIso(paymentData.paymentDate),
        referenceNumber: paymentData.referenceNumber,
        bankName: paymentData.bankName,
        notes: paymentData.notes,
        paymentAgreement,
        receiptDocument: documentId ? {
          fileId: documentId,
          fileName: receiptFile?.name,
          fileSize: receiptFile?.size,
          mimeType: receiptFile?.type,
          documentType: 'Payment Receipt'
        } : null
      };

      // Submit to ApplicationPayment if available, otherwise PaymentVerification
      let response;
      if (applicationPayment) {
        response = await api.post(`/crm/application-payments/public/${token}/submit`, payload);
      } else {
        response = await api.post(`/crm/payment-verification/public/${token}/submit`, payload);
      }

      toast.success('Payment submitted successfully! Your payment will be verified shortly.');
      
      // After submission, always show success screen
      // The backend sets paymentLinkClosed = true, so manually update state to trigger success UI
      const isInstallmentPayment = applicationPayment?.applicationPayment?.installmentPlan || 
                                   paymentVerification?.installmentPlan?.enabled;
      
      if (isInstallmentPayment) {
        // For installment payments, inform about next steps
        toast.success('Payment link has been closed. Staff will generate a new link for your next installment after verification.', {
          duration: 6000
        });
      }
      
      // Set state to show completion message instead of form
      if (applicationPayment) {
        // Update with response data if available, otherwise use submitted data
        const responseData = response?.data?.data;
        setApplicationPayment(prev => ({
          ...prev,
          applicationPayment: {
            ...prev.applicationPayment,
            status: responseData?.applicationPayment?.status || 'submitted',
            paymentDate: new Date(),
            amount: {
              ...prev.applicationPayment?.amount,
              ...responseData?.applicationPayment?.amount
            },
            installmentPlan: responseData?.applicationPayment?.installmentPlan || prev.applicationPayment?.installmentPlan,
            partialPayments: responseData?.applicationPayment?.partialPayments || prev.applicationPayment?.partialPayments || [],
            latestPartialPayment: responseData?.applicationPayment?.latestPartialPayment || prev.applicationPayment?.latestPartialPayment || null,
            // Store the submitted amount for display
            _submittedAmount: responseData?.applicationPayment?.submittedAmount || parseFloat(paymentData.actualAmount)
          },
          application: responseData?.application || prev.application
        }));
      } else {
        // Store the submitted amount to display in success screen
        setPaymentVerification(prev => ({
          ...prev,
          status: 'Payment Submitted',
          paymentLinkClosed: true,
          payment: {
            ...prev.payment,
            actualAmount: parseFloat(paymentData.actualAmount),
            paymentMethod: paymentData.paymentMethod,
            paymentDate: paymentData.paymentDate,
            referenceNumber: paymentData.referenceNumber
          }
        }));
      }
      
      // Clear form
      setPaymentData({
        actualAmount: '',
        paymentMethod: 'Bank Transfer',
        paymentDate: new Date().toISOString().split('T')[0],
        referenceNumber: '',
        bankName: '',
        notes: ''
      });
      setPaymentTermsAgreed(false);
      setReceiptFile(null);
    } catch (error) {
      isSubmittingRef.current = false;
      console.error('Payment submission error:', error);
      toast.error(error.response?.data?.message || 'Failed to submit payment. Please try again.');
    } finally {
      setSubmitting(false);
      setUploadingReceipt(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading payment information..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <ExclamationCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Go to Homepage
          </a>
        </div>
      </div>
    );
  }

  // If payment already submitted or link closed after partial payment
  // Show "Payment Submitted" page if:
  // 1. Payment was just submitted (status is 'submitted' and we have _submittedAmount in state)
  // 2. Status is 'approved' or 'payment-submitted' AND no remaining balance
  // 3. Status is 'Verified' (for PaymentVerification)
  // 4. paymentLinkClosed is true (for PaymentVerification)
  // Otherwise, show the form if there's a remaining balance
  
  // Calculate remaining balance
  // Prefer backend-provided remainingBalance for ApplicationPayment links (handles membership fee/discount).
  const expectedAmount = applicationPayment?.applicationPayment?.amount?.expectedAmount ||
                        paymentVerification?.payment?.expectedAmount || 0;
  const approvedAmount = applicationPayment?.applicationPayment?.amount?.approvedAmount || 0;
  const remainingBalance =
    typeof applicationPayment?.applicationPayment?.amount?.remainingBalance === 'number'
      ? applicationPayment.applicationPayment.amount.remainingBalance
      : (expectedAmount - approvedAmount);
  const hasRemainingBalance =
    Number(remainingBalance) > 0 || Number(paymentVerification?.actualRemainingAmount || 0) > 0;
  
  // Check if payment was just submitted (has _submittedAmount in state)
  const paymentJustSubmitted = applicationPayment?.applicationPayment?._submittedAmount !== undefined;
  
  // Check if status indicates payment was submitted
  const statusIsSubmitted = applicationPayment?.applicationPayment?.status === 'submitted' ||
                            applicationPayment?.applicationPayment?.status === 'payment-submitted' ||
                            applicationPayment?.applicationPayment?.status === 'approved' ||
                            paymentVerification?.status === 'Payment Submitted' ||
                            paymentVerification?.status === 'Verified';
  
  // Show success screen if:
  // 1. Payment was just submitted (has _submittedAmount) - always show success after submission
  // 2. Status is 'approved' or 'payment-submitted' AND no remaining balance
  // 3. Status is 'Verified' (for PaymentVerification)
  // 4. paymentLinkClosed is true (for PaymentVerification or ApplicationPayment public links)
  // Otherwise, show the form if there's a remaining balance
  const isPaymentSubmitted = paymentJustSubmitted ||
                             (statusIsSubmitted && !hasRemainingBalance) ||
                             applicationPayment?.applicationPayment?.paymentLinkClosed ||
                             gatewayCardReturnStatus === 'success' ||
                             paymentVerification?.status === 'Verified' || 
                             paymentVerification?.paymentLinkClosed;
  
  const appliedCreditDisplay = applyCredit ? roundCurrency(creditToApply) : 0;
  const cashAmountDisplay = roundCurrency(paymentData.actualAmount || 0);
  const totalCoveredDisplay = roundCurrency(cashAmountDisplay + appliedCreditDisplay);
  const isFullCreditSettle = applyCredit && appliedCreditDisplay > 0 && cashAmountDisplay === 0;
  const methodRequiresReceipt = !isFullCreditSettle && !['Card', 'Credit Card', 'Debit Card', 'Cash'].includes(paymentData.paymentMethod);

  const appPaymentData = applicationPayment?.applicationPayment;
  const appInstallments = Array.isArray(appPaymentData?.installmentPlan?.installments)
    ? appPaymentData.installmentPlan.installments
    : [];
  const appPartialPayments = Array.isArray(appPaymentData?.partialPayments)
    ? appPaymentData.partialPayments
    : [];

  const getInstallmentEffectivePaid = (inst) => {
    if (!inst) return 0;
    const installmentNo = Number(inst?.installmentNumber) || 0;
    const fromField = Number(inst?.paidAmount) || 0;
    const fromHistory = Array.isArray(inst?.paymentHistory)
      ? inst.paymentHistory
        .filter((p) => p?.status === 'Approved')
        .reduce((sum, p) => sum + (Number(p?.amount) || 0), 0)
      : 0;
    const fromPartials = installmentNo > 0
      ? appPartialPayments
        .filter((p) => p?.status === 'Approved' && Number(p?.installmentNumber) === installmentNo)
        .reduce((sum, p) => sum + (Number(p?.amount) || 0), 0)
      : 0;
    return roundCurrency(Math.max(fromField, fromHistory, fromPartials));
  };

  const intendedNo = Number(appPaymentData?.intendedInstallmentNumber) || 0;
  const currentInstallmentForForm = (intendedNo > 0
    ? appInstallments.find((inst) => Number(inst?.installmentNumber) === intendedNo)
    : null) || appInstallments.find((inst) => {
    const amount = Number(inst?.amount) || 0;
    if (amount <= 0) return false;
    const paid = getInstallmentEffectivePaid(inst);
    return roundCurrency(amount - paid) > 0.01;
  }) || null;

  const currentInstallmentPaidForForm = currentInstallmentForForm
    ? getInstallmentEffectivePaid(currentInstallmentForForm)
    : 0;

  const amountDueDisplay = roundCurrency(
    appPaymentData?.amount?.remainingBalance ||
    appPaymentData?.amount?.expectedAmount ||
    paymentVerification?.actualRemainingAmount ||
    paymentVerification?.payment?.expectedAmount || 0
  );
  const excessAmount = Math.max(0, roundCurrency(totalCoveredDisplay - amountDueDisplay));
  const currency = applicationPayment?.applicationPayment?.amount?.currency ||
                  paymentVerification?.payment?.currency || 'LKR';
  
  if (isPaymentSubmitted) {
    const isPartialPayment = applicationPayment?.applicationPayment?.installmentPlan ||
                            paymentVerification?.paymentLinkClosed || 
                            (paymentVerification?.installmentPlan?.enabled && paymentVerification?.status !== 'Verified');
    
    const paymentStatus = applicationPayment?.applicationPayment?.status || paymentVerification?.status;
    // Get the submitted amount:
    //  - Prefer _submittedAmount set in local state (manual/bank flows)
    //  - Then use last partial payment amount if available
    //  - Then fall back to lastPaymentAmount from backend (e.g. Paycorp card flows)
    //  - Finally fall back to cumulative paidAmount/actualAmount
    const paymentAmount =
      applicationPayment?.applicationPayment?._submittedAmount ??
      (applicationPayment?.applicationPayment?.partialPayments?.length > 0
        ? applicationPayment.applicationPayment.partialPayments[
            applicationPayment.applicationPayment.partialPayments.length - 1
          ]?.amount
        : null) ??
      applicationPayment?.applicationPayment?.lastPaymentAmount ??
      gatewaySubmittedAmount ??
      applicationPayment?.applicationPayment?.amount?.paidAmount ??
      paymentVerification?.payment?.actualAmount ??
      0;
    
    // Ensure we have a valid number
    const displayAmount = paymentAmount && paymentAmount > 0 ? paymentAmount : 0;
    const currency = applicationPayment?.applicationPayment?.amount?.currency ||
                    paymentVerification?.payment?.currency || 'LKR';
    const referenceNumber = applicationPayment?.application?.applicationNumber ||
                            paymentVerification?.payment?.referenceNumber;
    
    // Check if this is an installment payment - only show if there's actually an installment plan
    const hasInstallmentPlan = (applicationPayment?.applicationPayment?.installmentPlan && 
                                (applicationPayment.applicationPayment.installmentPlan.numberOfInstallments > 0 || 
                                 applicationPayment.applicationPayment.installmentPlan.installments?.length > 0)) ||
                               (paymentVerification?.installmentPlan?.enabled && 
                                paymentVerification.installmentPlan.installments?.length > 0);
    
    // Get installment information if available
    let installmentInfo = null;
    if (hasInstallmentPlan && applicationPayment?.applicationPayment?.installmentPlan) {
      const plan = applicationPayment.applicationPayment.installmentPlan;
      
      if (plan.installments && plan.installments.length > 0) {
        const partialPayments = Array.isArray(applicationPayment?.applicationPayment?.partialPayments)
          ? applicationPayment.applicationPayment.partialPayments
          : [];
        // Prefer backend-provided latest payment for this submission flow.
        // Fallback to array tail (append order) instead of timestamp sorting,
        // because manual/bank submissions may use date-only values (00:00) and
        // otherwise get incorrectly ordered behind older approved card payments.
        const latestSubmittedPayment =
          applicationPayment?.applicationPayment?.latestPartialPayment ||
          (partialPayments.length > 0
            ? partialPayments[partialPayments.length - 1]
            : null);
        const successfulCardInstallmentNo = Number(
          applicationPayment?.applicationPayment?.successfulCardPayment?.installmentNumber
        ) || 0;
        const intendedInstallmentNo = Number(
          applicationPayment?.applicationPayment?.intendedInstallmentNumber
        ) || 0;

        // Find which installment this payment is for
        const expectedAmount = applicationPayment.applicationPayment.amount?.expectedAmount;
        let matchedInstallment = null;

        // First preference: exact installment number from latest recorded payment.
        const latestInstallmentNo = Number(latestSubmittedPayment?.installmentNumber) || 0;
        if (latestInstallmentNo > 0) {
          matchedInstallment = plan.installments.find(
            (inst) => Number(inst?.installmentNumber) === latestInstallmentNo
          ) || null;
        }

        // For gateway-return success flows, backend tells us which installment
        // the successful card payment belongs to. Prefer that over "next unpaid".
        if (!matchedInstallment && gatewayCardReturnStatus === 'success' && successfulCardInstallmentNo > 0) {
          matchedInstallment = plan.installments.find(
            (inst) => Number(inst?.installmentNumber) === successfulCardInstallmentNo
          ) || null;
        }

        // If the page reloads before the card callback state is fully reflected,
        // fall back to the installment this payment link originally targeted.
        if (!matchedInstallment && intendedInstallmentNo > 0) {
          matchedInstallment = plan.installments.find(
            (inst) => Number(inst?.installmentNumber) === intendedInstallmentNo
          ) || null;
        }

        // Next preference: match submitted amount to an unpaid installment.
        // Useful for bank-transfer flows where the latest partial payment may
        // not yet have reliable installment metadata in early response states.
        if (!matchedInstallment && Number(displayAmount) > 0) {
          matchedInstallment = plan.installments.find((inst) => {
            const instAmount = Number(inst?.amount) || 0;
            const paidAmount = Number(inst?.paidAmount) || 0;
            const isUnpaid = (paidAmount + 0.01) < instAmount &&
              inst?.status !== 'Paid' &&
              inst?.status !== 'Verified';
            return isUnpaid && Math.abs(instAmount - Number(displayAmount)) < 0.01;
          }) || null;
        }
        
        // Next, try to find an installment that matches the expected amount
        if (!matchedInstallment && expectedAmount) {
          matchedInstallment = plan.installments.find(inst => 
            Math.abs(inst.amount - expectedAmount) < 0.01 // Account for floating point precision
          );
        }
        
        // If no match found by amount, find the first unpaid installment
        if (!matchedInstallment) {
          matchedInstallment = plan.installments.find(inst => 
            inst.status !== 'Paid' && inst.status !== 'Verified'
          );
        }
        
        // If all installments are paid, use the last installment
        if (!matchedInstallment && plan.installments.length > 0) {
          matchedInstallment = plan.installments[plan.installments.length - 1];
        }
        
        if (matchedInstallment) {
          installmentInfo = {
            current: matchedInstallment.installmentNumber || 1,
            total: plan.numberOfInstallments || plan.installments.length,
            installmentAmount: matchedInstallment.amount || 0
          };
        }
      }
    } else if (hasInstallmentPlan && paymentVerification?.installmentPlan) {
      installmentInfo = {
        current: paymentVerification.installmentPlan.currentInstallment || 1,
        total: paymentVerification.installmentPlan.installments?.length || 0
      };
    }
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {paymentStatus === 'Verified' || paymentStatus === 'approved' ? 'Payment Verified' : 'Payment Submitted'}
          </h2>
          <p className="text-gray-600 mb-6">
            {paymentStatus === 'Verified' || paymentStatus === 'approved'
              ? 'Your payment has been verified and processed successfully!'
              : isPartialPayment
              ? 'Your payment has been submitted successfully! This payment link has been closed. The institution will generate a new payment link for your next installment after verification.'
              : 'Your payment has been submitted and is being verified. You will be notified once the verification is complete.'}
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <p className="text-sm text-gray-600 mb-1">Amount Submitted</p>
            <p className="text-2xl font-bold text-gray-900">
              {currency} {displayAmount.toLocaleString()}
            </p>
            {referenceNumber && (
              <p className="text-sm text-gray-600 mt-3">
                Reference: <span className="font-medium">{referenceNumber}</span>
              </p>
            )}
            {hasInstallmentPlan && installmentInfo && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-2">Installment Plan Progress</p>
                <p className="text-sm font-medium text-gray-900">
                  Installment {installmentInfo.current} of {installmentInfo.total}
                  {installmentInfo.installmentAmount > 0 && (
                    <span className="text-gray-600 ml-2">
                      (Amount: {currency} {installmentInfo.installmentAmount.toLocaleString()})
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-6">
            Please check your email for updates on the verification status.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-6 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          <div className="flex items-center gap-3">
            <img src={getSlimLogoPath()} alt="SLIM ERP Logo" className="h-14 w-auto" />
            <div className='pl-4'>
              <h1 className="text-xl font-bold text-gray-900">Submit Payment</h1>
              <p className="text-sm text-gray-600">Submit your payment details and upload proof. Verification within 24-48 hours.</p>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left Column - Payment Information */}
          <div className="space-y-4">
            {/* Payment Amount Info */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Payment Information</h2>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-indigo-50 rounded-lg p-3">
                  <p className="text-xs text-gray-600 mb-1">Amount Due</p>
                  <p className="text-2xl font-bold text-indigo-600">
                    {applicationPayment?.applicationPayment?.amount?.currency || 
                     paymentVerification?.payment?.currency || 'LKR'} {
                      amountDueDisplay.toLocaleString()}
                  </p>
                  {(applicationPayment?.applicationPayment?.installmentPlan || paymentVerification?.installmentPlan?.enabled) && (() => {
                    const installmentNo = Number(currentInstallmentForForm?.installmentNumber) || null;
                    const alreadyPaid = currentInstallmentPaidForForm;
                    return installmentNo && alreadyPaid > 0 ? (
                      <p className="text-xs text-gray-600 mt-1">
                        Installment {installmentNo}: {applicationPayment?.applicationPayment?.amount?.currency || 'LKR'} {alreadyPaid.toLocaleString()} already paid
                      </p>
                    ) : null;
                  })()}
                </div>
                {(currentInstallmentForForm?.dueDate || paymentVerification?.dates?.paymentDueDate) && (
                  <div className="bg-orange-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">Due Date</p>
                    <p className="text-lg font-semibold text-orange-600">
                      {new Date(currentInstallmentForForm?.dueDate || 
                               paymentVerification.dates.paymentDueDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              {/* Fee Breakdown */}
              {paymentVerification?.breakdown && (
                <div className="border-t border-gray-200 pt-3">
                  <h3 className="text-xs font-semibold text-gray-700 mb-2">Fee Breakdown</h3>
                  <div className="space-y-1.5">
                    {paymentVerification.breakdown.registrationFee > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Registration Fee</span>
                        <span className="font-medium">{paymentVerification.payment?.currency || 'LKR'} {paymentVerification.breakdown.registrationFee.toLocaleString()}</span>
                      </div>
                    )}
                    {paymentVerification.breakdown.tuitionFee > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Tuition Fee</span>
                        <span className="font-medium">{paymentVerification.payment?.currency || 'LKR'} {paymentVerification.breakdown.tuitionFee.toLocaleString()}</span>
                      </div>
                    )}
                    {paymentVerification.breakdown.examFee > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Exam Fee</span>
                        <span className="font-medium">{paymentVerification.payment?.currency || 'LKR'} {paymentVerification.breakdown.examFee.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Available Credit Notice if student has credit balance */}
            {availableCredit > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-1.5">
                  <h3 className="text-xs font-semibold text-emerald-800 uppercase tracking-wider">Available Student Credit</h3>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                    Active Balance
                  </span>
                </div>
                <p className="text-2xl font-bold text-emerald-700">
                  {currency} {availableCredit.toLocaleString()}
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  {creditApplyDisabled
                    ? 'Applying credit online is currently unavailable. Please pay the full amount due.'
                    : 'You can apply this credit directly on the right to reduce or settle this payment.'}
                </p>
              </div>
            )}

            {/* Receipt Upload */}
            {methodRequiresReceipt && (
              <div className="bg-white rounded-lg shadow-md p-4">
                <ProofAttachmentDropzone
                  file={receiptFile}
                  onChange={setReceiptFile}
                  required
                  hint={null}
                />
              </div>
            )}

            {/* Help Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Need help?</strong> Contact our admissions office for any questions about the payment process.
              </p>
            </div>
          </div>

          {/* Right Column - Payment Form */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Payment Details</h2>
            
            <form onSubmit={handleSubmitPayment} className="space-y-3">
              {/* Available Student Credit Application */}
              {availableCredit > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3.5 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="apply-credit-toggle" className={`flex items-center gap-2 select-none ${creditApplyDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                      <input
                        id="apply-credit-toggle"
                        type="checkbox"
                        checked={applyCredit && !creditApplyDisabled}
                        disabled={creditApplyDisabled}
                        onChange={(e) => handleToggleApplyCredit(e.target.checked)}
                        className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                      />
                      <span className="text-xs font-semibold text-emerald-900">
                        Apply Student Credit Balance (Available: {currency} {availableCredit.toLocaleString()})
                      </span>
                    </label>
                  </div>
                  {applyCredit && (
                    <div className="space-y-2 pt-1 border-t border-emerald-200/60">
                      <div>
                        <label className="block text-xs font-medium text-emerald-800 mb-1">
                          Credit Amount to Apply (Max Applicable: {currency} {Math.min(availableCredit, amountDueDisplay).toLocaleString()})
                        </label>
                        <input
                          type="number"
                          value={creditToApply}
                          onChange={(e) => handleCreditToApplyChange(e.target.value)}
                          step="0.01"
                          min="0"
                          max={Math.min(availableCredit, amountDueDisplay)}
                          className="w-full px-2 py-1.5 text-sm border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white"
                          placeholder="Amount of credit to apply"
                        />
                      </div>
                      <div className="flex justify-between text-xs text-emerald-900 font-medium bg-emerald-100/70 p-2 rounded">
                        <span>Remaining Cash / Transfer Due:</span>
                        <span>{currency} {Math.max(0, roundCurrency(amountDueDisplay - creditToApply)).toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Excess Payment Notice */}
              {excessAmount > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1">
                  <div className="flex items-center gap-1.5 font-semibold text-amber-900">
                    <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Excess Payment Detected (+{currency} {excessAmount.toLocaleString()})</span>
                  </div>
                  <p>
                    You are submitting {currency} {totalCoveredDisplay.toLocaleString()}, which is {currency} {excessAmount.toLocaleString()} more than the remaining balance due ({currency} {amountDueDisplay.toLocaleString()}).
                    Once verified and approved by the Finance Team, this excess amount will be credited to your student account balance for future use.
                  </p>
                </div>
              )}

              {/* Amount & Payment Method Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {isFullCreditSettle ? 'Cash / Transfer Amount' : 'Amount Paid'} {!isFullCreditSettle && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="number"
                    name="actualAmount"
                    value={paymentData.actualAmount !== undefined ? paymentData.actualAmount : ''}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    required={!isFullCreditSettle}
                    disabled={submitting || uploadingReceipt}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Enter amount"
                  />
                  {isFullCreditSettle && (
                    <p className="mt-1 text-xs text-emerald-600 font-medium">
                      100% covered by credit balance
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Payment Method {!isFullCreditSettle && <span className="text-red-500">*</span>}
                  </label>
                  {isFullCreditSettle ? (
                    <div className="w-full px-2 py-1.5 text-sm border border-emerald-300 bg-emerald-50 text-emerald-800 rounded-lg font-medium">
                      Credit Balance
                    </div>
                  ) : (
                    <select
                      name="paymentMethod"
                      value={paymentData.paymentMethod}
                      onChange={handleInputChange}
                      required={!isFullCreditSettle}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="Bank Transfer">Bank Transfer</option>
                      {!applicationPayment?.onlinePaymentDisabled && (
                        <>
                          <option value="Online Payment">Online Payment</option>
                          <option value="Card" disabled>Card (Credit/Debit)</option>
                        </>
                      )}
                      <option value="Cheque">Cheque</option>
                      <option value="Mobile Payment">Mobile Payment</option>
                      <option value="Other">Other</option>
                    </select>
                  )}
                  {(applicationPayment?.onlinePaymentDisabled || applicationPayment?.cashPaymentDisabled) && !isFullCreditSettle && (
                    <p className="mt-1 text-xs text-amber-700">
                      Online card and cash are not available on this public link for franchise centers.
                      Transfer to the center bank account below, or ask center staff to record a manual payment.
                    </p>
                  )}
                </div>
              </div>

              {/* Payment Date */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Payment Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="paymentDate"
                  value={paymentData.paymentDate}
                  onChange={handleInputChange}
                  required
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Institution Bank Details (shown for bank/cheque payments) */}
              {(paymentData.paymentMethod === 'Bank Transfer' || paymentData.paymentMethod === 'Cheque') &&
                applicationPayment?.application?.bankAccountInfo?.accountNumber && (
                <div className="border border-gray-300 rounded-lg p-3 bg-indigo-50">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                    </svg>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-indigo-900 mb-1">Transfer Funds To</h4>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        <div><span className="text-indigo-700">Bank:</span> <span className="font-medium text-indigo-900">{applicationPayment.application.bankAccountInfo.bankName}</span></div>
                        <div><span className="text-indigo-700">Branch:</span> <span className="font-medium text-indigo-900">{applicationPayment.application.bankAccountInfo.branchName}</span></div>
                        <div><span className="text-indigo-700">Account Name:</span> <span className="font-medium text-indigo-900">{applicationPayment.application.bankAccountInfo.accountHolderName || applicationPayment.application.bankAccountInfo.accountName}</span></div>
                        <div><span className="text-indigo-700">Account No:</span> <span className="font-mono font-medium text-indigo-900">{applicationPayment.application.bankAccountInfo.accountNumber}</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Reference Number & Bank Name Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Reference Number (Application No.)
                  </label>
                  <input
                    type="text"
                    name="referenceNumber"
                    value={paymentData.referenceNumber}
                    readOnly
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                    placeholder="Application number"
                  />
                </div>
                {(paymentData.paymentMethod === 'Bank Transfer' || paymentData.paymentMethod === 'Cheque') && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      name="bankName"
                      value={paymentData.bankName}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Bank name"
                    />
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  name="notes"
                  value={paymentData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Any additional information about the payment"
                />
              </div>

              <div className={`border rounded-lg p-4 ${paymentTermsAgreed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentTermsAgreed}
                    onChange={(e) => setPaymentTermsAgreed(e.target.checked)}
                    className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    <strong>I understand and agree</strong> that this payment submission and any amount paid towards this application or enrollment are <strong>strictly non-refundable</strong> subject to{' '}
                    <button type="button" onClick={() => setShowPaymentPolicyModal(true)} className="text-indigo-600 underline hover:text-indigo-800 font-medium focus:outline-none">
                      institution policy
                    </button>. By continuing, I confirm that the payment amount, selected payment method, and any supporting documents I submit are accurate.
                  </span>
                </label>
                {!paymentTermsAgreed && (
                  <p className="mt-2 text-xs font-medium text-red-600">
                    * You must agree to this before submitting the payment.
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting || uploadingReceipt || !paymentTermsAgreed}
                  className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {submitting || uploadingReceipt ? (
                    <>
                      <LoadingSpinner inline size="sm" color="text-white" center={false} />
                      <span>{uploadingReceipt ? 'Uploading...' : 'Submitting...'}</span>
                    </>
                  ) : (
                    <>
                      <CreditCardIcon className="w-4 h-4" />
                      <span>Submit Payment</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    {/* Institution Payment Policy Modal */}
    {showPaymentPolicyModal && (
      <PolicyModal
        title="Institution Payment & Refund Policy"
        content={paymentPolicyText || DEFAULT_PAYMENT_POLICY}
        onClose={() => setShowPaymentPolicyModal(false)}
      />
    )}
    </>
  );
};

export default PublicPaymentSubmit;
